/**
 * Checkout 成功待機画面
 * - Stripe Checkout からリダイレクトされた後にポーリング
 * - active:true になったら /mypage へ遷移
 * - タイムアウト時はリロード促しメッセージを表示
 */
import { useAuth } from "@clerk/clerk-react";
import { useEffect, useRef, useState } from "react";
import { trackEvent } from "../lib/analytics";
import {
	clearLicenseCache,
	fetchLicenseState,
	type LicenseState,
} from "../lib/stripe/license";
import { pollLicense } from "../lib/stripe/pollLicense";

export default function CheckoutSuccess() {
	const { getToken, isSignedIn } = useAuth();
	const [timedOut, setTimedOut] = useState(false);
	const lastLicenseRef = useRef<LicenseState | null>(null);

	useEffect(() => {
		if (isSignedIn === undefined) return; // Clerk初期化待ち

		clearLicenseCache();

		const cleanup = pollLicense({
			fetchActive: async () => {
				const authToken = isSignedIn ? await getToken() : null;
				const state = await fetchLicenseState({
					forceRefresh: true,
					authToken,
				});
				lastLicenseRef.current = state;
				return state.active;
			},
			onActive: () => {
				const license = lastLicenseRef.current;
				const sessionId =
					new URLSearchParams(window.location.search).get("session_id") ??
					"unknown";
				trackEvent("purchase", {
					plan_name: license?.plan ?? "unknown",
					value: license?.plan === "yearly" ? 5000 : 500,
					currency: "JPY",
					transaction_id: sessionId,
				});
				window.location.href = "/mypage";
			},
			onTimeout: () => {
				setTimedOut(true);
			},
		});

		return cleanup;
	}, [isSignedIn, getToken]);

	return (
		<main className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
			<div className="max-w-sm w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
				{timedOut ? (
					<>
						<p className="text-lg font-bold text-gray-900 mb-2">
							反映に時間がかかっています
						</p>
						<p className="text-sm text-gray-500 mb-6">
							少し待ってからマイページをご確認ください。
						</p>
						<a
							href="/mypage"
							className="inline-block w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-xl transition-colors text-sm"
						>
							マイページへ
						</a>
					</>
				) : (
					<>
						<div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
						<p className="text-lg font-bold text-gray-900 mb-1">
							サブスクリプションを確認中...
						</p>
						<p className="text-sm text-gray-500">しばらくお待ちください</p>
					</>
				)}
			</div>
		</main>
	);
}
